import { useState } from "react";
import styles from "./DealerAnalytics.module.scss";
import { ACTIVITY_SERIES, ColumnChart, Legend } from "./charts";
import { Card, ReportFrame, SortTable, fmtNum, fmtUsd, pct, useReport } from "./shared";

const activityColumns = [
  { key: "views", label: "Views", num: true, bar: true },
  { key: "visitors", label: "Visitors", num: true },
  { key: "saves", label: "Saves", num: true },
  { key: "leads", label: "Leads", num: true, bar: true },
  { key: "inspections", label: "Inspections", num: true },
  { key: "sales", label: "Sales", num: true },
  {
    key: "leadRate",
    label: "Views → lead",
    num: true,
    sort: (r) => (r.views ? r.leads / r.views : -1),
    render: (r) => (r.views ? pct(r.leads, r.views) : "–"),
  },
];

// Each activity's share of its own total per band, so views (thousands) and
// leads (dozens) can sit on one scale and be compared by shape.
const shares = (rows) => {
  const totals = Object.fromEntries(ACTIVITY_SERIES.map((s) => [s.key, rows.reduce((t, r) => t + r[s.key], 0)]));
  return rows.map((r) => ({
    label: r.label,
    ...Object.fromEntries(ACTIVITY_SERIES.map((s) => [s.key, totals[s.key] ? Math.round((r[s.key] / totals[s.key]) * 1000) / 10 : 0])),
  }));
};

const ShowAll = ({ all, setAll, total, limit }) => (total > limit ? (
  <button type="button" className={styles.actionBtn} onClick={() => setAll(!all)}>
    {all ? `Top ${limit}` : `All ${total}`}
  </button>
) : null);

const Demand = ({ range, refreshKey }) => {
  const state = useReport("demand", range, refreshKey);
  const [allMakes, setAllMakes] = useState(false);
  const [allModels, setAllModels] = useState(false);

  return (
    <ReportFrame state={state}>
      {(d) => (
        <div className={styles.stack}>
          <Card
            title="Most popular models"
            sub="What people looked at, saved, asked about and bought. Sorted by leads; click a column to sort by it. Views → lead shows which models turn browsing into inquiries."
            actions={<ShowAll all={allModels} setAll={setAllModels} total={d.models.length} limit={20} />}
          >
            <SortTable
              rowKey={(r) => `${r.make}|${r.model}`}
              rows={d.models}
              limit={allModels ? 0 : 20}
              initialSort={{ key: "leads", dir: "desc" }}
              columns={[
                { key: "model", label: "Model", sort: (r) => `${r.make} ${r.model}`, render: (r) => <><span className={styles.muted}>{r.make}</span> {r.model}</> },
                ...activityColumns,
              ]}
            />
          </Card>

          <Card
            title="Most popular makes"
            actions={<ShowAll all={allMakes} setAll={setAllMakes} total={d.makes.length} limit={15} />}
          >
            <SortTable
              rowKey={(r) => r.make}
              rows={d.makes}
              limit={allMakes ? 0 : 15}
              initialSort={{ key: "leads", dir: "desc" }}
              columns={[{ key: "make", label: "Make" }, ...activityColumns]}
            />
          </Card>

          <div className={styles.grid2}>
            <Card
              title="Price people are shopping at"
              sub={`Share of each activity by the car's dollar price on the site. Median dealer sale this period: ${fmtUsd(d.medianSalePrice)}.`}
            >
              <Legend items={ACTIVITY_SERIES} />
              <div style={{ marginTop: 10 }}>
                <ColumnChart rows={shares(d.priceBands)} series={ACTIVITY_SERIES} format={(v) => `${v}%`} />
              </div>
              <p className={`${styles.small} ${styles.muted}`}>
                No price known: {fmtNum(d.unknown.price.views)} views, {fmtNum(d.unknown.price.saves)} saves, {fmtNum(d.unknown.price.leads)} leads (ASK listings and general inquiries).
              </p>
            </Card>
            <Card
              title="Model years people want"
              sub="Share of each activity by model year."
            >
              <Legend items={ACTIVITY_SERIES} />
              <div style={{ marginTop: 10 }}>
                <ColumnChart rows={shares(d.yearBands)} series={ACTIVITY_SERIES} format={(v) => `${v}%`} />
              </div>
              <p className={`${styles.small} ${styles.muted}`}>
                Importable today under the 25-year rule (by model year): views {d.importable.views ?? "–"}%, saves {d.importable.saves ?? "–"}%, leads {d.importable.leads ?? "–"}%.
              </p>
            </Card>
          </div>
        </div>
      )}
    </ReportFrame>
  );
};

export default Demand;
